import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  IL_COLORS,
  formatCurrency,
  formatSignedCurrency,
  formatSignedPercent,
} from "../../../../constants/investmentLeague";

const COLS = [
  { key: "stock",         label: "MAC/Group",         align: "left"  },
  { key: "invested",      label: "Invested",      align: "right" },
  { key: "current",       label: "Current Value", align: "right" },
  { key: "returns",       label: "Returns ($)",   align: "right" },
  { key: "returnsPct",    label: "Returns (%)",   align: "right" },
];

const cellSx = (align) => ({
  textAlign:  align,
  py:         1.5,
  px:         { xs: 1.5, md: 2 },
  fontSize:   "12.5px",
  whiteSpace: "nowrap",
});

const HoldingsTable = ({ holdings = [], totals, highlightBest }) => {
  const theme = useTheme();
  const t     = theme.custom;

  const rowBg = (holding) => {
    if (highlightBest && holding.buUnit === highlightBest) return IL_COLORS.positiveBg;
    return "transparent";
  };

  const totalsColor = totals?.returns >= 0 ? IL_COLORS.positive : IL_COLORS.negative;

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          width:          "100%",
          minWidth:       "640px",
          borderCollapse: "collapse",
        }}
      >
        <Box component="thead">
          <Box component="tr" sx={{ background: t.surface2 }}>
            {COLS.map((col) => (
              <Box
                component="th"
                key={col.key}
                sx={{
                  ...cellSx(col.align),
                  fontSize:      "10px",
                  fontWeight:    600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color:         t.text3,
                  borderBottom:  `1px solid ${t.border}`,
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {holdings.map((holding) => {
            const color = holding.positive ? IL_COLORS.positive : IL_COLORS.negative;
            return (
              <Box
                component="tr"
                key={holding.buUnit || `${holding.ticker}-${holding.name}`}
                sx={{ background: rowBg(holding) }}
              >
                <Box component="td" sx={cellSx("left")}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width:        8,
                        height:       8,
                        borderRadius: "50%",
                        background:   color,
                        flexShrink:   0,
                      }}
                    />
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: t.text }}>
                          {holding.ticker}
                        </Typography>
                        {highlightBest && holding.buUnit === highlightBest && (
                          <Box
                            sx={{
                              px:           0.75,
                              py:           0.25,
                              borderRadius: "4px",
                              background:   IL_COLORS.positive,
                              color:        "#fff",
                              fontSize:     "9px",
                              fontWeight:   700,
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              lineHeight:   1,
                            }}
                          >
                            Best
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: "11px", color: t.text3 }}>
                        {holding.name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                  {formatCurrency(holding.invested)}
                </Box>
                <Box component="td" sx={{ ...cellSx("right"), color: t.text, fontWeight: 500 }}>
                  {formatCurrency(holding.current)}
                </Box>
                <Box component="td" sx={{ ...cellSx("right"), color, fontWeight: 600 }}>
                  {formatSignedCurrency(holding.returns)}
                </Box>
                <Box component="td" sx={{ ...cellSx("right"), color, fontWeight: 600 }}>
                  {formatSignedPercent(holding.returnsPct)}
                </Box>
              </Box>
            );
          })}
        </Box>

        {totals && (
          <Box component="tfoot">
            <Box
              component="tr"
              sx={{
                background: t.surface3,
                borderTop:  `2px solid ${t.border2}`,
              }}
            >
              <Box
                component="td"
                sx={{ ...cellSx("left"), fontWeight: 700, color: t.text }}
              >
                Total
              </Box>
              <Box component="td" sx={{ ...cellSx("right"), fontWeight: 700, color: t.text }}>
                {formatCurrency(totals.invested)}
              </Box>
              <Box component="td" sx={{ ...cellSx("right"), fontWeight: 700, color: t.text }}>
                {formatCurrency(totals.current)}
              </Box>
              <Box
                component="td"
                sx={{
                  ...cellSx("right"),
                  fontWeight: 700,
                  color:      totalsColor,
                }}
              >
                {formatSignedCurrency(totals.returns)}
              </Box>
              <Box
                component="td"
                sx={{
                  ...cellSx("right"),
                  fontWeight: 700,
                  color:      totalsColor,
                }}
              >
                {formatSignedPercent(totals.returnsPct)}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HoldingsTable;
